/**
 * CodeBlock - Syntax highlighted code block with copy button
 */

import { useState, useCallback } from 'react'
import { useTheme } from 'next-themes'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { oneLight, oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism'
import { Copy, Check } from 'lucide-react'
import clsx from 'clsx'
import { useTranslation } from '@/i18n'

interface CodeBlockProps {
  children: string
  language?: string
  className?: string
}

// Map common language aliases
const languageMap: Record<string, string> = {
  js: 'javascript',
  ts: 'typescript',
  py: 'python',
  rb: 'ruby',
  sh: 'bash',
  shell: 'bash',
  yml: 'yaml',
  md: 'markdown',
}

export function CodeBlock({ children, language, className }: CodeBlockProps) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const { resolvedTheme } = useTheme()
  const isDark = resolvedTheme === 'dark'

  // Extract language from className (e.g., "language-javascript")
  const extractedLang = className?.replace('language-', '') || language || 'text'
  const normalizedLang = languageMap[extractedLang] || extractedLang

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(children)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      console.error('Failed to copy code:', error)
    }
  }, [children])

  return (
    <div className="relative group rounded-lg overflow-hidden my-3">
      {/* Header with language and copy button */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-100 dark:bg-dark-accent border-b border-gray-200 dark:border-dark-border">
        <span className="text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase">
          {normalizedLang}
        </span>
        <button
          onClick={handleCopy}
          className={clsx(
            'flex items-center gap-1 px-2 py-1 rounded text-xs',
            'transition-colors',
            copied
              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
              : 'hover:bg-gray-200 dark:hover:bg-dark-border text-gray-600 dark:text-gray-400'
          )}
        >
          {copied ? (
            <>
              <Check className="h-3.5 w-3.5" />
              {t('chatv2:code.copied', { defaultValue: 'Copied!' })}
            </>
          ) : (
            <>
              <Copy className="h-3.5 w-3.5" />
              {t('chatv2:code.copy', { defaultValue: 'Copy code' })}
            </>
          )}
        </button>
      </div>

      {/* Code content */}
      <SyntaxHighlighter
        language={normalizedLang}
        style={isDark ? oneDark : oneLight}
        customStyle={{
          margin: 0,
          padding: '1rem',
          fontSize: '0.875rem',
          lineHeight: '1.5',
          backgroundColor: isDark ? 'var(--dark-muted)' : '#f9fafb',
        }}
        wrapLines
        wrapLongLines
      >
        {children.trim()}
      </SyntaxHighlighter>
    </div>
  )
}

export default CodeBlock
